/**
 * @module useReportsRoutes
 * @description Hook for managing reports navigation routes and links
 */
import { useSelector } from 'react-redux';
import { translate } from '../../../utils/locale';
import { getIsContractOwner, hasGroupAccess } from 'features/app/redux/context';
import { getEmbargo } from 'features/app/redux/context';
import { getFeature } from 'features/app/redux/features';
import { getUserRoles } from 'features/app/redux/context';
import { knowledgeBaseRoutes } from '../../common/knowledgeBaseRoutes';

/**
 * Hook that returns the navigation routes for reports and downloads
 * @returns {Object} Navigation route configuration object
 */
const useConnectReportsRoutes = (): {} => {
	// Manage Data Conditional logic
	const permissions: string[] | undefined = useSelector(getUserRoles);
	const embargoFeature = useSelector(getFeature('embargo_feature'));
	const embargoPeriod = useSelector(getEmbargo);
	const applyEmbargo = embargoFeature && embargoPeriod?.some((x) => x.value);

	// Reports Route Definitions
	const isContractOwner = useSelector(getIsContractOwner);
	const canHaveGroupAccess = useSelector(hasGroupAccess);

	///Checks for embargo, checks for null permissions and finally
	// checks the permission passed is in the permissions array
	const permissionsCheck = (permission: string): boolean => {
		if (applyEmbargo) return false;

		if (!permissions || permissions.length === 0) return false;

		return permissions.includes(permission, 0);
	};

	const isConsortium = !!isContractOwner && !canHaveGroupAccess;
	const isSchool = !isContractOwner;

	/**
	 * Array of reports navigation route configurations
	 * @type {SecondaryNavigationLinks[]}
	 */
	const reportsConnectRoutes: SecondaryNavigationLinks[] = [
		{
			displayName: translate('home.singleSchool.reportsDownloads.links.MEGS') as string,
			icon: 'file',
			withInternalUrl: '/reports/meg-reports',
			isVisible: !applyEmbargo,
		},
		{
			displayName: translate('home.singleSchool.reportsDownloads.links.MY_REPORTS') as string,
			icon: 'file',
			withInternalUrl: '/reports/my-reports',
			isVisible:
				(isSchool && permissionsCheck('DataExchange_View')) ||
				(isConsortium && permissionsCheck('DataExchange_View')),
		},
		{
			isVisible: isContractOwner && permissionsCheck('DataExchange_View'),
			withInternalUrl: `/groups/reports/school-and-college-reports`,
			icon: 'file',
			displayName: translate('home.groups.reportsDownloads.links.SCHOOL_COLLEGE') as string,
		},
		{
			displayName: translate(
				'home.singleSchool.reportsDownloads.links.ADDITIONAL_DOWNLOADS'
			) as string,
			icon: 'file',
			withInternalUrl: '/reports/additional-downloads',
			isVisible: permissionsCheck('DataExchange_View'),
		},
	];

	/**
	 * Home navigation route configuration with flyout menu
	 * @type {SecondaryNavigationLinks}
	 */
	const reportsConnectHomeRoutes: SecondaryNavigationLinks = {
		displayName: translate('home.singleSchool.reportsDownloads.HEADING') as string,
		isVisible: true,
		disabled: false,
		icon: 'gear',
		flyoutContent: [
			{
				heading: translate('home.singleSchool.reportsDownloads.HEADING') as string,
				flyoutLinks: reportsConnectRoutes,
			},
			knowledgeBaseRoutes,
		],
	};

	return {
		reportsConnectRoutes,
		reportsConnectHomeRoutes,
	};
};

export default useConnectReportsRoutes;
